// Mobile menu: each section has its own soft, natural background tone. While a section is
// open, the whole menu panel fades to that tone - the top bar and the section behind it
// change together, at the same moment and speed - and back to the theme's background on the
// main list. Mobile only. Loaded from snippets/menu-curated-collections.liquid.
//
// The open section is tracked in script and its tone set directly on the menu panel (rather
// than with a CSS :has() rule), because some phone browsers - Safari in particular - don't
// always repaint a :has() change straight away, which left the top bar the old colour until
// the page was scrolled.

// Tone per menu section, by the menu item's handle (Homeware -> homeware,
// Tea & Teaware -> tea-teaware). All at about the same lightness, each a different natural
// hue so neighbouring sections are easy to tell apart. Sections not listed keep the theme's
// background.
const SECTION_TONES = {
  'popular-collections': '#ece3e0', // blush linen
  homeware: '#e9dfcf', // sand
  kitchenware: '#e4e3cd', // hinoki straw
  tableware: '#ecdcd3', // clay
  'tea-teaware': '#dee5d8', // matcha
  bathware: '#dbe3e6', // mist
  accessories: '#e4dee7', // dusk lavender
  makers: '#e8e1d5', // washi
  about: '#e4e3df', // stone
};

// How strongly the tones show: 100 = the colours above, 0 = the theme's plain background
const STRENGTH = 40;
// How long the fade takes (seconds)
const FADE = 0.3;

const STYLE_ID = 'mujo-menu-tones-css';

const mix = (tone) => `color-mix(in srgb, ${tone} ${STRENGTH}%, var(--color-background))`;

// The panel and every section read the same colour, with the same fade, so they always move
// together
const CSS = `
@media screen and (max-width: 989px) {
  .menu-drawer,
  .menu-drawer .menu-drawer__submenu[id^='link-'] {
    background-color: var(--mujo-drawer-tone, var(--color-background));
    transition: background-color ${FADE}s ease;
  }
  .menu-drawer .mujo-drawer-bar {
    background-color: transparent;
  }
}
@media (prefers-reduced-motion: reduce) {
  .menu-drawer,
  .menu-drawer .menu-drawer__submenu[id^='link-'] { transition: none; }
}
`;

const ensureStyles = () => {
  const existing = document.getElementById(STYLE_ID);
  if (existing) {
    if (existing.textContent !== CSS) existing.textContent = CSS;
    return;
  }
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.append(style);
};

const setTone = (drawer, raw) => {
  const tone = raw ? mix(raw) : '';
  if ((drawer.style.getPropertyValue('--mujo-drawer-tone') || '') === tone) return;
  if (tone) drawer.style.setProperty('--mujo-drawer-tone', tone);
  else drawer.style.removeProperty('--mujo-drawer-tone');
};

/** Set the menu panel's tone to match whichever section is open (or clear it). */
const sync = () => {
  const drawer = document.querySelector('.menu-drawer');
  if (!drawer) return;
  const openSections = drawer.querySelectorAll(
    '.menu-drawer__menu-container.menu-open > .menu-drawer__submenu[id^="link-"]'
  );
  const section = openSections[openSections.length - 1];
  setTone(drawer, section ? SECTION_TONES[section.id.slice(5)] : undefined);
};

/** Start the fade the instant a section (or the back button) is tapped. */
const onTap = (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const drawer = target?.closest('.menu-drawer');
  if (!drawer) return;
  if (target.closest('.menu-drawer__back-button')) {
    setTone(drawer, undefined);
    return;
  }
  const opener = target.closest('summary, button, a');
  const container = opener?.closest('.menu-drawer__menu-container');
  const section = container?.querySelector(':scope > .menu-drawer__submenu[id^="link-"]');
  if (section && !section.contains(opener)) setTone(drawer, SECTION_TONES[section.id.slice(5)]);
};

if (!window.__mujoMenuTones) {
  window.__mujoMenuTones = true;
  ensureStyles();
  sync();

  document.addEventListener('click', onTap, { capture: true, passive: true });

  // Keep in step with the menu's own state (closing the menu, the theme re-rendering it)
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      sync();
    });
  }).observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'open'],
  });
}